import { FunctionComponent, useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import {
  getAllCounties,
  getCompetenceAreas,
  getMunicipalitiesByCounty,
  saveProfileImage,
  updateUser,
} from 'Frontend/scripts/services/profile.service'
import { ICompetenceArea, ICounty, IMunicipality, IProfileUser } from 'Frontend/scripts/models/profile.model'
import { ISelectOption } from 'Frontend/scripts/models/forums.model'
import MultiSelectComponent from 'Features/MultiSelectComponent/MultiSelectComponent'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { useTranslation } from 'react-i18next'

interface EditProfilePageProps {
  user: IProfileUser
  callback: () => void
}

interface EditProfileForm {
  firstname: string
  lastname: string
  image: string
  phone: string
  mobilePhone: string
  postalCode: string
  authority: string
  title: string
  county: ISelectOption
  municipality: ISelectOption[]
  competence: ISelectOption[]
}

const EditProfilePage: FunctionComponent<EditProfilePageProps> = ({ user, callback }) => {
  const [image, setImage] = useState<File | null>(null)
  const [counties, setCounties] = useState<ISelectOption[]>([])
  const [municipalities, setMunicipalities] = useState<ISelectOption[]>([])
  const [competences, setCompetences] = useState<ISelectOption[]>([])
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<EditProfileForm>({
    defaultValues: {
      firstname: user.firstname,
      lastname: user.lastname,
      phone: user.phone,
      mobilePhone: user.mobilePhone,
      postalCode: user.postalAddress,
      authority: user.authority,
      title: user.title,
      image: '',
      county: {
        value: user.county?.id.toString(),
        label: user.county?.name,
      },
      municipality: user.municipalities?.map((municipality) => {
        return { value: municipality.id.toString(), label: municipality.name }
      }),
      competence: user.competenceAreas?.map((competence) => {
        return { value: competence.id.toString(), label: competence.name }
      }),
    },
  })

  const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0])
    }
  }

  const onSubmit = (data: EditProfileForm) => {
    const muncicipalityIdList = data.municipality?.map((item: ISelectOption) => Number(item.value))
    const competences = data.competence?.map((item: ISelectOption) => Number(item.value))

    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      updateUser(
        token.csrfToken,
        user.id,
        data.firstname,
        data.lastname,
        data.phone,
        data.authority,
        data.title,
        parseInt(data.county.value),
        muncicipalityIdList,
        competences,
        data.mobilePhone,
        data.postalCode,
        user.email,
      ).then(() => {
        if (image != null) {
          saveProfileImage(token.csrfToken, user.id, image).then(() => {
            callback()
          })
        } else {
          callback()
        }
      })
    })
  }

  const updateMunicipalities = (countyId: string) => {
    getMunicipalities(countyId)
    setValue('municipality', [], { shouldValidate: true })
  }

  const getMunicipalities = (countyId: string) => {
    getMunicipalitiesByCounty(countyId).then((municipalitiesData: IMunicipality[]) => {
      const convertedMunicipalityArray = municipalitiesData?.map((municipality) => ({
        value: municipality.id.toString(),
        label: municipality.name,
      }))
      setMunicipalities(convertedMunicipalityArray)
    })
  }

  useEffect(() => {
    getAllCounties().then((countiesData: ICounty[]) => {
      const convertedCountiesArray = countiesData.map((county) => ({
        value: county.id.toString(),
        label: county.name,
      }))
      setCounties(convertedCountiesArray)
    })

    getCompetenceAreas().then((competences: ICompetenceArea[]) => {
      const convertedCompetencesArray = competences.map((competence) => ({
        value: competence.id.toString(),
        label: competence.name,
      }))
      setCompetences(convertedCompetencesArray)
    })
  }, [])

  useEffect(() => {
    if (counties.length > 0 && user?.county?.id) {
      getMunicipalities(user.county.id.toString())
    }
  }, [counties])

  return (
    <div className="livstecknet__form-area p-3">
      <h1 className="h2">{t('edit-profile')}</h1>
      <p className="profile-page-edit__mandatory-field-explanation">{t('mandatory-field-explanation')}</p>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Form.Group>
          <Form.Label htmlFor="inputFirstname">{t('firstname')}*</Form.Label>
          <Form.Control
            id="inputFirstname"
            type="text"
            {...register('firstname', { required: t('error-add-firstname') })}
          />
          {errors.firstname && <Form.Text className="text-danger">{errors.firstname.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputLastname">{t('lastname')}*</Form.Label>
          <Form.Control
            id="inputLastname"
            type="text"
            {...register('lastname', { required: t('error-add-lastname') })}
          />
          {errors.lastname && <Form.Text className="text-danger">{errors.lastname.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputImage">{t('image')}</Form.Label>
          <Form.Control
            id="inputImage"
            type="file"
            accept="image/*"
            {...register('image')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleImage(e)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputPhone">{t('phone')}*</Form.Label>
          <Form.Control id="inputPhone" type="text" {...register('phone', { required: t('error-add-phone') })} />
          {errors.phone && <Form.Text className="text-danger">{errors.phone.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputMobile">{t('mobile-phone')}</Form.Label>
          <Form.Control id="inputMobile" type="text" {...register('mobilePhone')} />
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputPostalCode">{t('postal-code')}</Form.Label>
          <Form.Control id="inputPostalCode" type="text" {...register('postalCode')} />
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="inputAuthority">{t('authority')}*</Form.Label>
          <Form.Control
            id="inputAuthority"
            type="text"
            {...register('authority', { required: t('error-add-authority') })}
          />
          {errors.authority && <Form.Text className="text-danger">{errors.authority.message}</Form.Text>}
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="inputTitle">{t('title')}</Form.Label>
          <Form.Control id="inputTitle" type="text" {...register('title', { required: t('error-add-title') })} />
          {errors.title && <Form.Text className="text-danger">{errors.title.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="county">{t('county')}</Form.Label>
          <Controller
            name="county"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                inputId="county"
                options={counties}
                placeholder={t('error-choose-county')}
                classNamePrefix="single-select"
                onChange={(value) => {
                  field.onChange(value)
                  value && updateMunicipalities(value.value.toString())
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    fontSize: '1.6rem',
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    fontSize: '1.6rem',
                  }),
                }}
              />
            )}
          />
          {errors.county && <Form.Text className="text-danger">{errors.county.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="municipality">{t('municipality')}</Form.Label>
          <Controller
            name="municipality"
            control={control}
            render={({ field }) => (
              <MultiSelectComponent
                key={JSON.stringify(field.value)}
                options={municipalities}
                addToUrl={false}
                placeholderText={t('choose-municipality')}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
          {errors.county && <Form.Text className="text-danger">{errors.county.message}</Form.Text>}
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="competence">{t('competence-areas')}</Form.Label>
          <Controller
            name="competence"
            control={control}
            render={({ field }) => (
              <MultiSelectComponent
                options={competences}
                addToUrl={false}
                placeholderText={t('choose-competence')}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
          {errors.competence && <Form.Text className="text-danger">{errors.competence.message}</Form.Text>}
        </Form.Group>
        <div className="d-flex gap-3 mt-3">
          <Button variant="secondary" type="submit">
            {t('save-profile')}
          </Button>
          <Button variant="outline-secondary" type="button" onClick={() => callback()}>
            {t('abort')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default EditProfilePage
